<template>
  <div class="monthly-finance-summary">
    <v-row>
      <v-col cols="12" lg="2">
        <div class="monthly-finance-summary__card">
          <div class="monthly-finance-summary__card-title">Project Members</div>
          <div class="monthly-finance-summary__card-data">
            <div class="monthly-finance-summary__value">
              {{ summary.contracts_size ? summary.contracts_size : 0 }}
            </div>
          </div>
        </div>
      </v-col>

      <v-col cols="12" lg="">
        <div class="monthly-finance-summary__card">
          <div class="monthly-finance-summary__card-title">Total worked</div>
          <div class="monthly-finance-summary__card-data">
            <div class="monthly-finance-summary__value">
              {{ summary.time ? summary.time : '00:00:00' }}
            </div>
            <div v-if="summary.time_change"
              class="monthly-finance-summary__trend"
              :class="{ 'monthly-finance-summary__trend--decrease':
                isNegative(summary.time_change) }"
            >
              <div class="monthly-finance-summary__trend-val">
                {{ formattedChangeString(summary.time_change) }}
              </div>
              <v-icon
                class="monthly-finance-summary__trend-ico"
                :class="{ 'monthly-finance-summary__trend-ico--decrease':
                  isNegative(summary.time_change) }"
                medium
              >
                mdi-arrow-up-bold
              </v-icon>
            </div>
          </div>
        </div>
      </v-col>

      <v-col cols="12" lg="">
        <div class="monthly-finance-summary__card">
          <div class="monthly-finance-summary__card-title">Price</div>
          <div class="monthly-finance-summary__card-data">
            <div class="monthly-finance-summary__value">
              {{ summary.amount ? summary.amount : '0' }} PLN
            </div>
            <div v-if="summary.amount_change"
              class="monthly-finance-summary__trend"
              :class="{ 'monthly-finance-summary__trend--decrease':
                summary.amount_change < 0 }"
            >
              <div class="monthly-finance-summary__trend-val">
                {{ formattedChangeString(summary.amount_change) }}
              </div>
              <v-icon
                class="monthly-finance-summary__trend-ico"
                :class="{ 'monthly-finance-summary__trend-ico--decrease':
                  summary.amount_change < 0 }"
                medium
              >
                mdi-arrow-up-bold
              </v-icon>
            </div>
          </div>
        </div>
      </v-col>

      <v-col class="monthly-finance-summary__col-picker" cols="12" lg="">
        <div
          class="monthly-finance-summary__month-picker"
          :class="{ 'monthly-finance-summary__month-picker--complete': isMonthComplete }"
          v-click-outside="closeMenu"
        >
          <div class="monthly-finance-summary__card-title white--text">Billing months</div>
          <div class="monthly-finance-summary__drop" @click="toggleMonthMenu">
            <div class="monthly-finance-summary__month">
              {{ currentMonth.label }}
            </div>
            <v-icon
              v-if="availableMonths.length"
              class="monthly-finance-summary__drop-ico"
              :class="{ 'monthly-finance-summary__drop-ico--open': isMonthPickerMenuVisible }"
              medium
            >
              mdi-chevron-down
            </v-icon>
          </div>
        </div>

        <div
          v-show="isMonthPickerMenuVisible"
          class="monthly-finance-summary__month-menu"
        >
          <div
            v-for="(month, index) in availableMonths" :key="index"
            class="monthly-finance-summary__menu-element"
            @click="changeDate(month.month)"
          >
            <span>{{ month.label }}</span>
            <span
              class="monthly-finance-summary__menu-status"
              :class="{ 'monthly-finance-summary__menu-status--complete':
                month.status === 'Complete' }"
            >
              <v-icon v-if="month.status === 'Complete'" x-small color="green">mdi-check</v-icon>
              <v-icon v-else x-small color="orange">mdi-refresh</v-icon>
            </span>
          </div>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import vClickOutside from 'v-click-outside';

export default {
  props: {
    summary: {
      type: Object,
      required: true,
    },
    availableMonths: {
      type: Array,
      required: true,
    },
    currentMonth: {
      type: Object,
      required: true,
    },
  },

  directives: {
    clickOutside: vClickOutside.directive,
  },

  data() {
    return {
      date: '',
      isMonthPickerMenuVisible: false,
    };
  },

  computed: {
    isMonthComplete() {
      return this.currentMonth.status === 'Complete';
    },
  },

  methods: {
    formattedChangeString(changeVal) {
      return changeVal < 0 ? `${changeVal}%` : `+ ${changeVal}%`;
    },

    isNegative(val) {
      return val < 0;
    },

    toggleMonthMenu() {
      if (this.availableMonths.length) {
        this.isMonthPickerMenuVisible = !this.isMonthPickerMenuVisible;
      }
    },

    closeMenu() {
      this.isMonthPickerMenuVisible = false;
    },

    changeDate(date) {
      this.$emit('change', date);
      this.isMonthPickerMenuVisible = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.monthly-finance-summary {
  &__card {
    padding: 16px;
    background: #FFFFFF;
    box-shadow: 0px 3px 6px #00000029;
    border-radius: 8px;
    height: 100%;
  }

  &__month-picker {
    padding: 16px;
    color: #FFFFFF;
    background: $color--yellow;
    box-shadow: 0px 3px 6px #00000029;
    border-radius: 8px;
    height: 100%;

    &--complete {
      background: $color--green;
    }
  }

  &__card-title {
    color: $main--headers;
    font-size: 12px;
    line-height: 14px;
    font-weight: 500;
    margin-bottom: 8px;
  }

  &__card-data {
    display: flex;
    justify-content: space-between;
  }

  &__value {
    color: $color--blue;
    font-size: 18px;
    font-weight: 600;
    line-height: 21px;
  }

  &__trend {
    display: flex;
    align-items: center;
    color: $color--green;
    margin-left: 16px;

    &--decrease {
      color: $color--red;
    }
  }

  &__trend-val {
    font-size: 12px;
    line-height: 14px;
    font-weight: 600;
    margin-right: 4px;
  }

  &__trend-ico {
    color: inherit;

    &--decrease {
      transform: rotate(180deg);
    }
  }

  &__col-picker {
    position: relative;
  }

  &__drop {
    display: flex;
    justify-content: space-between;
    cursor: pointer;
  }

  &__month {
    color: white;
    font-size: 18px;
    font-weight: 600;
    line-height: 21px;
  }

  &__drop-ico {
    color: white;
    transition: all 0.3s ease;

    &--open {
      transform: rotate(180deg);
    }
  }

  &__month-menu {
    position: absolute;
    padding: 20px;
    background: #FFFFFF;
    box-shadow: 0px 3px 6px #00000029;
    border-radius: 8px;
    margin-top: 8px;
    width: calc(100% - 24px);
    z-index: 11;
  }

  &__menu-element {
    cursor: pointer;
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    justify-content: space-between;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__menu-status {
    background: $color--orange-light;
    color: $color--orange;
    width: 20px;
    height: 20px;
    border-radius: 8px;
    line-height: 20px;
    text-align: center;

    &--complete {
      background: $color--green--light;
      color: $color--green;
    }
  }
}
</style>
